<template>
  <div>
    <navbar :name="userTitle" :selected-item="selectedNavbarItem" @logout="logout" />
    <el-container width="100%">
      <sidebar :is-collapse="isCollapse" />
      <div class="nuxt_container" :class="isShiftManagementShowPage ? 'shift-management-show-page' : ''">
        <div class="sidebar-button" @click="flipSidebar()">
          <div v-if="isCollapse">
            <img src="~/assets/icons/sidebar_open_icon.svg">
          </div>
          <div v-if="!isCollapse">
            <img src="~/assets/icons/sidebar_close_icon.svg">
          </div>
        </div>
        <application-error-alert />
        <slot />
        <auto-shift-loading v-if="autoShiftProcessing" />
      </div>
    </el-container>
    <dev-mark />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter, useNuxtApp } from '#app'
import { ElMessage } from 'element-plus'
import { apiWebNurseryMePath, destroyApiWebUserSessionPath } from '../rails/routes'
import Navbar from '~/components/Navbar.vue'
import { SelectedItem } from '~/components/NavbarSelectedItem'
import AutoShiftLoading from '~/components/shift_management/AutoShiftLoading.vue'
import Sidebar from '~/components/Sidebar.vue'
import { portalLogoutUrl } from '~/helpers/constUrls'
import ApplicationErrorAlert from '~/components/ApplicationErrorAlert.vue'
import DevMark from '~/components/DevMark.vue'
import { useMe } from '~/composables/useMe'
import { useAutoShift } from '~/composables/useAutoShift'

const { $axios, $gtm, $rollbar } = useNuxtApp() as any
const route = useRoute()
const router = useRouter()
const { me, setId, setName, setUserTitle, setIsBeta, setIsReferenceRequired, setPermissions, setDemo } = useMe()
const { state: autoShiftState, startPolling } = useAutoShift()

const isCollapse = ref(false)

const userTitle = computed(() => me.value.userTitle)
const autoShiftProcessing = computed(() => autoShiftState.value.processing)

onMounted(async () => {
  const isCollapseLS = typeof window !== 'undefined' ? (localStorage.getItem('isSidebarCollapse') || '0') : '0'
  isCollapse.value = Boolean(parseInt(isCollapseLS, 10))

  // 参照シフトが無い限り、毎回リクエストする
  if (!me.value.name || me.value.isReferenceRequired) {
    try {
      const resMe = await ($axios as any).$get(apiWebNurseryMePath()) as {
        id: number;
        userTitle: string;
        nurseryId: number;
        name: string;
        isBeta: boolean;
        isReferenceRequired: boolean;
        userType: string;
        permissions: string[];
        demo: boolean;
      }
      setId(resMe.id)
      setName(resMe.name)
      setIsReferenceRequired(resMe.isReferenceRequired)
      setUserTitle(resMe.userTitle)
      setPermissions(resMe.permissions)
      setDemo(resMe.demo)
      const nurseryId = resMe.nurseryId
      const client = resMe.name
      localStorage.setItem('userType', resMe.userType)
      $gtm?.pushEvent({ event: 'setClientName', clientName: client, nurseryId })

      // 10分周期でセッションの有効状態をチェックする
      // 無効(401)な場合、axiosで処理され総合管理ログインページへリダイレクトされる
      setInterval(() => {
        ($axios as any).$get(apiWebNurseryMePath())
      }, 600000)
    } catch (e: any) {
      if (e.response?.status === 401) { return }

      console.error(e)
      $rollbar?.error(e)
    }
  }
})

const logout = async (): Promise<void> => {
  const res = await $axios.delete(destroyApiWebUserSessionPath())
  if (res.status === 200) {
    // 前回ログイン時isGuest取得できる場合は対象ログイン画面にredirect
    if (localStorage.getItem('isGuest') === 'true') {
      router.push('/guest_login')
    } else {
      window.location.href = portalLogoutUrl()
    }
  } else {
    ElMessage({
      message: 'ログアウトに失敗しました',
      type: 'error'
    })
  }
}

// NOTE: headerでフォーカスがあたっているメニューを返す
const selectedNavbarItem = computed<SelectedItem>(() => {
  const shiftRegex = /^\/nursery\/shift_managements/
  const workRegex = /^\/nursery\/works/
  const settingRegex = /^\/nursery\/settings/

  if (shiftRegex.test(route.path)) {
    return 'shift'
  } else if (workRegex.test(route.path)) {
    return 'work'
  } else if (settingRegex.test(route.path)) {
    return 'setting'
  } else {
    return 'top'
  }
})

// シフト表ページだけは下余白をなくしたいので、クラス名で制御する
const isShiftManagementShowPage = computed<boolean>(() => {
  return /^\/nursery\/shift_managements\/[0-9]+$/.test(route.path)
})

const flipSidebar = (): void => {
  isCollapse.value = !isCollapse.value
  localStorage.setItem('isSidebarCollapse', Number(isCollapse.value).toString())
}
</script>

<style lang="sass" scoped>
.nuxt_container
  position: relative
  padding: 5px 40px 40px 40px
  min-width: 1024px
  width: 100%
  &.shift-management-show-page
    padding-bottom: 0
.sidebar-button
  position: absolute
  left: 0px
.sidebar-button
  background-color: #FBFBF7
  cursor: pointer
</style>
